import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, Loader2, Sparkles } from "lucide-react";
import api from "../../lib/api";

const SUGGESTIONS = [
  "How did you calculate my estimate?",
  "Why did the three AI appraisers disagree?",
  "How much do I keep after selling?",
  "How much do I save with the 1% listing fee?",
];

export default function EstimateChat({ property, comparables, netSheet, panel }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    const history = messages;
    setMessages((m) => [...m, { role: "user", content: question }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/property/chat", {
        property, comparables, net_sheet: netSheet, panel: panel?.ok ? panel : null, history, message: question,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.answer || "" }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — something went wrong. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-brand-hairline bg-white p-6 sm:p-8 shadow-[0_4px_20px_rgba(27,59,43,0.04)]"
      data-testid="estimate-chat-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-emeraldLight">
          <MessageCircle className="h-4 w-4 text-brand-emerald" />
        </span>
        <span className="eyebrow">Ask About Your Estimate</span>
      </div>
      <p className="mt-2 text-sm text-brand-stone">
        Questions answered only from your property, comparable sales, and net-sheet numbers.
      </p>

      <div ref={listRef} className="mt-5 space-y-3 max-h-[22rem] overflow-y-auto pr-1" data-testid="estimate-chat-messages">
        {messages.length === 0 && (
          <div className="rounded-xl bg-brand-cream/60 p-4 text-sm text-brand-stone flex gap-2">
            <Sparkles className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
            <span>Ask me anything about your valuation — how it was calculated, your net proceeds, or your savings.</span>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`chat-message-${m.role}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-brand-emerald text-brand-cream rounded-br-sm"
                : "bg-brand-cream text-brand-ink rounded-bl-sm"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start" data-testid="chat-loading">
            <div className="rounded-2xl rounded-bl-sm bg-brand-cream px-4 py-2.5 text-brand-stone flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-brand-emerald" />
              <span className="text-sm">Checking your numbers…</span>
            </div>
          </div>
        )}
      </div>

      {messages.length === 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => ask(s)} disabled={loading} data-testid="chat-suggestion-chip"
              className="rounded-full border border-brand-hairline bg-white px-3 py-1.5 text-xs text-brand-stone hover:border-brand-emerald hover:text-brand-emerald transition-colors disabled:opacity-50">
              {s}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-4 flex items-center gap-2">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question about your estimate…"
          className="flex-1 rounded-xl border border-brand-hairline bg-brand-cream/40 px-4 py-3 text-sm text-brand-ink outline-none focus:ring-1 focus:ring-brand-emerald"
        />
        <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send-button"
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-emerald text-brand-cream hover:bg-brand-emeraldHover transition-colors disabled:opacity-50">
          <Send className="h-4 w-4" />
        </button>
      </form>
      <p className="mt-3 text-[0.7rem] uppercase tracking-wider text-brand-stone/70">
        AI assistant · Grounded in your data · Not an appraisal
      </p>
    </div>
  );
}
